import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { Marketplace } from '@/enums/marketplace';
import { SelectOption } from '@/ui/Select';

export const REPORT_FILTER_ALL_VALUE = '__all__';

const REPORT_FILTER_ALL_OPTION: SelectOption = {
    name: 'Все',
    value: REPORT_FILTER_ALL_VALUE,
};

const compareRuText = (leftValue: string, rightValue: string): number =>
    leftValue.localeCompare(rightValue, 'ru', {
        sensitivity: 'base',
        numeric: true,
    });

const sanitizeFilterValues = (values: string[], availableValueSet: Set<string>): string[] => [
    ...new Set(
        values
            .map((value) => value.trim())
            .filter((value) => value === REPORT_FILTER_ALL_VALUE || availableValueSet.has(value)),
    ),
];

const areFilterSelectionsEqual = (leftValues: string[], rightValues: string[]): boolean =>
    leftValues.length === rightValues.length && leftValues.every((value, index) => value === rightValues[index]);

export const buildReportPlatformOptions = (): SelectOption[] => [
    REPORT_FILTER_ALL_OPTION,
    { name: 'WB', value: Marketplace.Wb },
    { name: 'Ozon', value: Marketplace.Ozon },
];

export const buildReportProductGroupOptions = (products: ProductGroupEntity[]): SelectOption[] => [
    REPORT_FILTER_ALL_OPTION,
    ...[...products]
        .sort(
            (leftProduct, rightProduct) =>
                compareRuText(leftProduct.name, rightProduct.name) ||
                compareRuText(leftProduct.brandName || '', rightProduct.brandName || '') ||
                leftProduct.id.localeCompare(rightProduct.id),
        )
        .map((product) => ({
            name: product.name,
            value: product.id,
            info: product.brandName || undefined,
        })),
];

export const buildReportBrandGroupOptions = (brandGroups: BrandGroupEntity[]): SelectOption[] => [
    REPORT_FILTER_ALL_OPTION,
    ...[...brandGroups]
        .sort(
            (leftBrandGroup, rightBrandGroup) =>
                compareRuText(leftBrandGroup.name, rightBrandGroup.name) ||
                leftBrandGroup.id.localeCompare(rightBrandGroup.id),
        )
        .map((brandGroup) => ({
            name: brandGroup.name,
            value: brandGroup.id,
        })),
];

export const normalizeNextReportFilterSelection = ({
    currentValues,
    nextValues,
    availableValues,
}: {
    currentValues: string[];
    nextValues: string[];
    availableValues: string[];
}): string[] => {
    const availableValueSet = new Set(availableValues);
    const sanitizedCurrentValues = sanitizeFilterValues(currentValues, availableValueSet);
    const sanitizedNextValues = sanitizeFilterValues(nextValues, availableValueSet);

    if (!sanitizedNextValues.length) {
        return currentValues.includes(REPORT_FILTER_ALL_VALUE) ? currentValues : [REPORT_FILTER_ALL_VALUE];
    }

    const nextIncludesAll = sanitizedNextValues.includes(REPORT_FILTER_ALL_VALUE);
    if (nextIncludesAll) {
        const currentIncludesAll = sanitizedCurrentValues.includes(REPORT_FILTER_ALL_VALUE);
        if (currentIncludesAll) {
            const specificValues = sanitizedNextValues.filter((value) => value !== REPORT_FILTER_ALL_VALUE);
            if (!specificValues.length) {
                return currentValues.includes(REPORT_FILTER_ALL_VALUE) ? currentValues : [REPORT_FILTER_ALL_VALUE];
            }

            return areFilterSelectionsEqual(currentValues, specificValues) ? currentValues : specificValues;
        }

        return [REPORT_FILTER_ALL_VALUE];
    }

    return areFilterSelectionsEqual(currentValues, sanitizedNextValues) ? currentValues : sanitizedNextValues;
};

export const reconcileReportFilterSelection = ({
    currentValues,
    availableValues,
}: {
    currentValues: string[];
    availableValues: string[];
}): string[] => {
    const availableValueSet = new Set(availableValues);
    const sanitizedCurrentValues = sanitizeFilterValues(currentValues, availableValueSet);

    if (!sanitizedCurrentValues.length || sanitizedCurrentValues.includes(REPORT_FILTER_ALL_VALUE)) {
        return currentValues.length === 1 && currentValues[0] === REPORT_FILTER_ALL_VALUE
            ? currentValues
            : [REPORT_FILTER_ALL_VALUE];
    }

    return areFilterSelectionsEqual(currentValues, sanitizedCurrentValues) ? currentValues : sanitizedCurrentValues;
};

export const resolveAppliedReportFilterValues = <TValue extends string>(
    selectedValues: TValue[],
): TValue[] | undefined => {
    const normalizedValues = [...new Set(selectedValues.map((value) => value.trim()).filter(Boolean))] as TValue[];

    if (!normalizedValues.length || normalizedValues.some((value) => value === REPORT_FILTER_ALL_VALUE)) {
        return undefined;
    }

    return normalizedValues;
};

export const resolveReportFilterSelectionLabels = ({
    options,
    selectedValues,
}: {
    options: SelectOption[];
    selectedValues: string[];
}): string[] => {
    const normalizedValues = [...new Set(selectedValues.map((value) => value.trim()).filter(Boolean))];

    if (!normalizedValues.length || normalizedValues.includes(REPORT_FILTER_ALL_VALUE)) {
        return [REPORT_FILTER_ALL_OPTION.name];
    }

    const selectedValueSet = new Set(normalizedValues);
    const resolvedLabels = options
        .filter((option) => option.value !== REPORT_FILTER_ALL_VALUE && selectedValueSet.has(option.value))
        .map((option) => option.name);

    return resolvedLabels.length ? resolvedLabels : [REPORT_FILTER_ALL_OPTION.name];
};
